import type {
  PlaybookId,
  PlaybookView,
} from "@repo/app-types/entities/playbook";
import type { PlaybookViewDefaults } from "@repo/app-types/entities/playbookViewDefaults";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import type { FC } from "react";
import { useUpdatePlaybookViewDefaultsMutation } from "../../api/entities/playbook.js";

interface SetPlaybookViewDefaultActionProps {
  onClick: () => void;
}

export const SetPlaybookViewDefaultAction: FC<
  SetPlaybookViewDefaultActionProps
> = (props) => (
  <Button
    className="w-full justify-center"
    size={Size.SMALL}
    variant={ButtonVariant.Secondary}
    onClick={props.onClick}
  >
    Set default for everyone
  </Button>
);

interface SetPlaybookViewDefaultConfirmationProps {
  playbookId: PlaybookId;
  view: PlaybookView;
  /** Only the settings being saved; other defaults remain unchanged. */
  defaults: PlaybookViewDefaults;
  kind?: "filters" | "columns";
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

/**
 * Saves the current filters or columns as the playbook's default for
 * this kind of page.
 */
export const SetPlaybookViewDefaultConfirmation: FC<
  SetPlaybookViewDefaultConfirmationProps
> = (props) => {
  const {
    playbookId,
    view,
    defaults,
    kind = "filters",
    isOpen,
    onOpenChange,
  } = props;
  const [updateViewDefaults, { isLoading: isSaving }] =
    useUpdatePlaybookViewDefaultsMutation();

  const handleConfirm = async () => {
    try {
      await updateViewDefaults({ id: playbookId, view, defaults }).unwrap();

      onOpenChange(false);
      showToast({
        title: `Default ${kind} updated for everyone`,
        variant: ToastVariant.Success,
      });
    } catch {
      showToast({
        title: `Couldn't update the default ${kind}`,
        variant: ToastVariant.Error,
      });
    }
  };

  return (
    <SimpleModal
      isOpen={isOpen}
      variant={SimpleModalVariant.Standard}
      title={`Set these ${kind} as the default for everyone?`}
      subtitle={
        kind === "columns"
          ? `Teammates without saved column preferences will start with these columns on the ${view === "calls" ? "Calls" : "Team"} pages for this playbook. Everyone can still customize their own columns.`
          : view === "calls"
            ? "Teammates will start with these filters on the Calls pages for this playbook. They can still make changes and reset to this default."
            : "Teammates will start with these filters on the performance pages for this playbook. They can still make changes and reset to this default."
      }
      confirmButtonText="Set default"
      isPrimaryButtonLoading={isSaving}
      onCancel={() => onOpenChange(false)}
      onConfirm={() => void handleConfirm()}
    />
  );
};
